import { createServerFn } from "@tanstack/react-start";

export interface FxRatesResult {
  rates: Record<string, number>;
  source: "exchangerate-api" | "frankfurter" | "fallback";
  fetchedAt: string; // ISO
  upstreamUpdatedAt: string | null; // ISO
  stale: boolean;
}

// Fallback: refreshed to reflect roughly-current levels (2026). Used only when
// both upstream APIs fail. Better to be a few percent off than a year off.
const FALLBACK_RATES: Record<string, number> = {
  USD: 1, EUR: 0.87, GBP: 0.74, CAD: 1.40, AUD: 1.43, JPY: 162, CHF: 0.81,
  SEK: 9.7, NOK: 10.2, DKK: 6.5, NZD: 1.62, SGD: 1.29, HKD: 7.80, MXN: 17.4,
  BRL: 5.08, ZAR: 18.0, INR: 96.4, CNY: 6.78, KRW: 1370, PLN: 3.70,
};

// Per-worker-instance in-memory cache. Best-effort; a cold start refetches.
let cache: { at: number; value: FxRatesResult } | null = null;
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

async function fetchExchangeRateApi(): Promise<{ rates: Record<string, number>; updatedAt: string | null } | null> {
  try {
    const r = await fetch("https://open.er-api.com/v6/latest/USD");
    if (!r.ok) return null;
    const j = (await r.json()) as { rates?: Record<string, number>; time_last_update_utc?: string };
    if (!j?.rates) return null;
    return { rates: j.rates, updatedAt: j.time_last_update_utc ? new Date(j.time_last_update_utc).toISOString() : null };
  } catch {
    return null;
  }
}

async function fetchFrankfurter(): Promise<{ rates: Record<string, number>; updatedAt: string | null } | null> {
  try {
    const r = await fetch("https://api.frankfurter.dev/latest?from=USD");
    if (!r.ok) return null;
    const j = (await r.json()) as { rates?: Record<string, number>; date?: string };
    if (!j?.rates) return null;
    return { rates: { USD: 1, ...j.rates }, updatedAt: j.date ? new Date(j.date + "T00:00:00Z").toISOString() : null };
  } catch {
    return null;
  }
}

export const getFxRates = createServerFn({ method: "GET" }).handler(async (): Promise<FxRatesResult> => {
  const now = Date.now();
  if (cache && now - cache.at < CACHE_TTL_MS) return cache.value;

  const [primary, secondary] = await Promise.all([fetchExchangeRateApi(), fetchFrankfurter()]);

  let result: FxRatesResult;

  if (primary && secondary) {
    // Sanity check: if any overlapping currency disagrees by >3%, prefer Frankfurter (ECB).
    let disagreement = false;
    for (const [k, v] of Object.entries(secondary.rates)) {
      const p = primary.rates[k];
      if (!p || !v) continue;
      if (Math.abs(p - v) / v > 0.03) {
        disagreement = true;
        console.warn(`[fx-rates] disagreement on ${k}: exchangerate-api=${p} frankfurter=${v}`);
      }
    }
    if (disagreement) {
      result = {
        rates: secondary.rates,
        source: "frankfurter",
        fetchedAt: new Date().toISOString(),
        upstreamUpdatedAt: secondary.updatedAt,
        stale: false,
      };
    } else {
      result = {
        rates: primary.rates,
        source: "exchangerate-api",
        fetchedAt: new Date().toISOString(),
        upstreamUpdatedAt: primary.updatedAt,
        stale: false,
      };
    }
  } else if (primary) {
    result = { rates: primary.rates, source: "exchangerate-api", fetchedAt: new Date().toISOString(), upstreamUpdatedAt: primary.updatedAt, stale: false };
  } else if (secondary) {
    result = { rates: secondary.rates, source: "frankfurter", fetchedAt: new Date().toISOString(), upstreamUpdatedAt: secondary.updatedAt, stale: false };
  } else {
    result = { rates: FALLBACK_RATES, source: "fallback", fetchedAt: new Date().toISOString(), upstreamUpdatedAt: null, stale: true };
  }

  // Only cache successful fetches; retry on next call if we're on fallback.
  if (result.source !== "fallback") cache = { at: now, value: result };
  return result;
});
